import type { AgingBucketParsed, ProcessedMonthRow } from './dataParser'

/** Result of aggregating multiple months — same shape as v1 `aggregateMonthlyData`. */
export type AggregatedMonthSnapshot = Omit<ProcessedMonthRow, 'invoice_month'> & {
	invoice_month: number | string
	month_label: string
}

/**
 * v1 aggregateMonthlyData — merge multiple month rows for quarter/half/year/all-month views.
 */
export function aggregateMonthlyData(
	monthlyData: ProcessedMonthRow[]
): AggregatedMonthSnapshot | null {
	if (!Array.isArray(monthlyData) || monthlyData.length === 0) {
		return null
	}

	const aggregated: AggregatedMonthSnapshot = {
		...monthlyData[0],
		invoice_year: monthlyData[0].invoice_year,
		invoice_month: 'all',
		invoice_month_start: monthlyData[0].period_start_date || monthlyData[0].invoice_month_start,
		period_start_date: monthlyData[0].period_start_date,
		month_label: `All Months ${monthlyData[0].invoice_year}`,
		active_clients: 0,
		month_total_invoices: 0,
		month_paid_invoices: 0,
		month_outstanding_invoices: 0,
		month_draft_invoices: 0,
		month_cancelled_invoices: 0,
		month_total_invoiced_amount: 0,
		month_total_paid_amount: 0,
		month_total_outstanding_amount: 0,
		month_overdue_invoices: 0,
		month_overdue_amount: 0,
		overdue_invoices: 0,
		overdue_amount: 0,
		outstanding_amount: 0,
		not_yet_due_amount: 0,
		total_overdue_customers: 0,
		month_avg_invoice_size: 0,
		month_payment_rate: 0,
		overall_avg_days_overdue: 0,
		high_risk_customers: 0,
		customers_with_multiple_overdue: 0,
		month_collection_rate: 0,
		period_collection_rate: monthlyData[0].period_collection_rate || 0,
		monthly_client_details: [],
		clients_with_overdue_details: '[]',
		top_multiple_overdue_customers: [],
		top_overdue_invoices_detail: [],
		aging_bucket_breakdown: [],
		total_deposit_amount: 0,
		deposit_invoice_count: 0,
		total_deposit_adjustment: 0,
		deposit_adjustment_line_count: 0,
		net_deposit_balance: 0,
		overall_collection_risk: monthlyData[0].overall_collection_risk,
		overdue_percentage_of_outstanding: monthlyData[0].overdue_percentage_of_outstanding,
	}

	for (const month of monthlyData) {
		aggregated.active_clients += month.active_clients || 0
		aggregated.month_total_invoices += month.month_total_invoices || 0
		aggregated.month_paid_invoices += month.month_paid_invoices || 0
		aggregated.month_outstanding_invoices += month.month_outstanding_invoices || 0
		aggregated.month_draft_invoices += month.month_draft_invoices || 0
		aggregated.month_cancelled_invoices += month.month_cancelled_invoices || 0
		aggregated.month_total_invoiced_amount += month.month_total_invoiced_amount || 0
		aggregated.month_total_paid_amount += month.month_total_paid_amount || 0
		aggregated.month_total_outstanding_amount +=
			month.month_total_outstanding_amount || month.outstanding_amount || 0
		aggregated.month_overdue_invoices +=
			month.month_overdue_invoices || month.overdue_invoices || 0
		aggregated.month_overdue_amount +=
			month.month_overdue_amount || month.overdue_amount || 0
		aggregated.overdue_invoices += month.overdue_invoices || 0
		aggregated.overdue_amount += month.overdue_amount || 0
		aggregated.outstanding_amount += month.outstanding_amount || 0
		aggregated.not_yet_due_amount += month.not_yet_due_amount || 0
		aggregated.total_overdue_customers += month.total_overdue_customers || 0
		aggregated.high_risk_customers += month.high_risk_customers || 0
		aggregated.customers_with_multiple_overdue += month.customers_with_multiple_overdue || 0
	}

	if (monthlyData.length > 0) {
		const first = monthlyData[0]
		if (first.total_deposit_amount != null) {
			aggregated.total_deposit_amount = Number(first.total_deposit_amount)
		}
		if (first.deposit_invoice_count != null) {
			aggregated.deposit_invoice_count = Number(first.deposit_invoice_count)
		}
		if (first.total_deposit_adjustment != null) {
			aggregated.total_deposit_adjustment = Number(first.total_deposit_adjustment)
		}
		if (first.deposit_adjustment_line_count != null) {
			aggregated.deposit_adjustment_line_count = Number(first.deposit_adjustment_line_count)
		}
		if (first.net_deposit_balance != null) {
			aggregated.net_deposit_balance = Number(first.net_deposit_balance)
		}
	}

	if (monthlyData.length > 0) {
		aggregated.overall_avg_days_overdue =
			monthlyData.reduce((s, m) => s + (m.overall_avg_days_overdue || 0), 0) /
			monthlyData.length
		aggregated.month_collection_rate =
			monthlyData.reduce((s, m) => s + (m.month_collection_rate || 0), 0) /
			monthlyData.length
	}

	if (aggregated.month_total_invoiced_amount > 0) {
		aggregated.month_payment_rate =
			(aggregated.month_total_paid_amount / aggregated.month_total_invoiced_amount) * 100
	}

	if (aggregated.month_total_invoices > 0) {
		aggregated.month_avg_invoice_size =
			aggregated.month_total_invoiced_amount / aggregated.month_total_invoices
	}

	const clientMap = new Map<string | number, Record<string, unknown>>()
	for (const month of monthlyData) {
		let clients: Record<string, unknown>[] = []
		if (month.clients_with_overdue_details) {
			try {
				if (typeof month.clients_with_overdue_details === 'string') {
					clients = JSON.parse(month.clients_with_overdue_details) as Record<
						string,
						unknown
					>[]
				} else if (Array.isArray(month.clients_with_overdue_details)) {
					clients = month.clients_with_overdue_details as Record<string, unknown>[]
				}
			} catch {
				clients = []
			}
		}
		if (clients.length === 0 && Array.isArray(month.monthly_client_details)) {
			clients = month.monthly_client_details
		}

		for (const client of clients) {
			const clientName = String(client.contact_name || client.client_name || 'Unknown')
			const rawContactId = client.contact_id
			const clientId: string | number =
				typeof rawContactId === 'string' || typeof rawContactId === 'number'
					? rawContactId
					: clientName

			if (clientMap.has(clientId)) {
				const existing = clientMap.get(clientId)!
				existing.total_invoices =
					Number(existing.total_invoices) + Number(client.total_invoices || 0)
				existing.paid_invoices =
					Number(existing.paid_invoices) + Number(client.paid_invoices || 0)
				existing.outstanding_invoices =
					Number(existing.outstanding_invoices) +
					Number(client.outstanding_invoices || 0)
				existing.overdue_invoices =
					Number(existing.overdue_invoices) +
					Number(client.overdue_invoices || client.overdue_invoice_count || 0)
				existing.overdue_invoice_count =
					Number(existing.overdue_invoice_count) +
					Number(client.overdue_invoice_count || client.overdue_invoices || 0)
				existing.total_amount =
					Number(existing.total_amount) +
					Number(client.total_invoiced_amount || client.total_amount || 0)
				existing.total_invoiced_amount =
					Number(existing.total_invoiced_amount) +
					Number(client.total_invoiced_amount || client.total_amount || 0)
				existing.paid_amount =
					Number(existing.paid_amount) +
					Number(client.total_paid_amount || client.paid_amount || 0)
				existing.total_paid_amount =
					Number(existing.total_paid_amount) +
					Number(client.total_paid_amount || client.paid_amount || 0)
				existing.outstanding_amount =
					Number(existing.outstanding_amount) +
					Number(client.total_outstanding_amount || client.outstanding_amount || 0)
				existing.total_outstanding_amount =
					Number(existing.total_outstanding_amount) +
					Number(client.total_outstanding_amount || client.outstanding_amount || 0)
				existing.overdue_amount =
					Number(existing.overdue_amount) + Number(client.overdue_amount || 0)
				existing.overdue_amount_original =
					Number(existing.overdue_amount_original || 0) +
					Number(client.overdue_amount_original || 0)
				existing.total_outstanding_amount_original =
					Number(existing.total_outstanding_amount_original || 0) +
					Number(
						client.total_outstanding_amount_original ||
							client.outstanding_amount_original ||
							0
					)
				if (client.deposit_amount != null) existing.deposit_amount = client.deposit_amount
				if (client.deposit_adjustment != null) {
					existing.deposit_adjustment = client.deposit_adjustment
				}
				if (client.deposit_adjustment_line_count != null) {
					existing.deposit_adjustment_line_count = client.deposit_adjustment_line_count
				}
				if (client.net_deposit_balance != null) {
					existing.net_deposit_balance = client.net_deposit_balance
				}
				const totalAmt = Number(existing.total_invoiced_amount || existing.total_amount)
				if (totalAmt > 0) {
					existing.payment_rate =
						(Number(existing.total_paid_amount || existing.paid_amount) / totalAmt) * 100
				}
			} else {
				clientMap.set(clientId, {
					...client,
					client_name: clientName,
					contact_name: clientName,
					contact_id: clientId,
					overdue_invoices:
						Number(client.overdue_invoice_count || client.overdue_invoices || 0),
					overdue_invoice_count:
						Number(client.overdue_invoice_count || client.overdue_invoices || 0),
					total_amount: Number(client.total_invoiced_amount || client.total_amount || 0),
					total_invoiced_amount: Number(
						client.total_invoiced_amount || client.total_amount || 0
					),
					paid_amount: Number(client.total_paid_amount || client.paid_amount || 0),
					total_paid_amount: Number(client.total_paid_amount || client.paid_amount || 0),
					outstanding_amount: Number(
						client.total_outstanding_amount || client.outstanding_amount || 0
					),
					total_outstanding_amount: Number(
						client.total_outstanding_amount || client.outstanding_amount || 0
					),
				})
			}
		}
	}

	aggregated.monthly_client_details = Array.from(clientMap.values())
	aggregated.clients_with_overdue_details = JSON.stringify(aggregated.monthly_client_details)

	const customerMap = new Map<string, Record<string, unknown>>()
	for (const month of monthlyData) {
		if (Array.isArray(month.top_multiple_overdue_customers)) {
			for (const customer of month.top_multiple_overdue_customers) {
				const c = customer as Record<string, unknown>
				const customerName = String(c.name)
				if (customerMap.has(customerName)) {
					const existing = customerMap.get(customerName)!
					existing.overdue_count =
						Number(existing.overdue_count) + Number(c.overdue_count || 0)
					existing.overdue_amount =
						Number(existing.overdue_amount) + Number(c.overdue_amount || 0)
					existing.max_days_overdue = Math.max(
						Number(existing.max_days_overdue || 0),
						Number(c.max_days_overdue || 0)
					)
				} else {
					customerMap.set(customerName, { ...c })
				}
			}
		}
	}
	aggregated.top_multiple_overdue_customers = Array.from(customerMap.values()).sort(
		(a, b) => Number(b.overdue_amount) - Number(a.overdue_amount)
	)

	const invoiceMap = new Map<string, Record<string, unknown>>()
	for (const month of monthlyData) {
		if (Array.isArray(month.top_overdue_invoices_detail)) {
			for (const invoice of month.top_overdue_invoices_detail) {
				const inv = invoice as Record<string, unknown>
				const invoiceId = String(inv.invoice_id)
				if (!invoiceMap.has(invoiceId)) {
					invoiceMap.set(invoiceId, { ...inv })
				}
			}
		}
	}
	aggregated.top_overdue_invoices_detail = Array.from(invoiceMap.values()).sort(
		(a, b) => Number(b.amount) - Number(a.amount)
	)

	const bucketMap = new Map<string, AgingBucketParsed>()
	for (const month of monthlyData) {
		if (Array.isArray(month.aging_bucket_breakdown)) {
			for (const bucket of month.aging_bucket_breakdown) {
				const bucketRange = bucket.range
				if (bucketMap.has(bucketRange)) {
					const existing = bucketMap.get(bucketRange)!
					existing.count += bucket.count || 0
					existing.amount += bucket.amount || 0
					existing.percentage =
						((existing.count / aggregated.month_overdue_invoices) * 100) || 0
				} else {
					bucketMap.set(bucketRange, { ...bucket })
				}
			}
		}
	}
	aggregated.aging_bucket_breakdown = Array.from(bucketMap.values())

	return aggregated
}
